import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Avatar,
  InputBase,
  Card,
  CardContent,
  CardActions,
  Chip,
  Grid,
  Alert,
  CircularProgress,
  Menu,
  MenuItem,
  Checkbox,
  Drawer,
  Divider,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import {
  Add as AddIcon,
  GridView as GridViewIcon,
  ViewList as ViewListIcon,
  Business as BusinessIcon,
  SearchOutlined,
  MoreVert,
  EditOutlined,
  DeleteOutline,
  FileDownloadOutlined,
  FilterListOutlined,
  Close,
} from "@mui/icons-material";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../store/Index";
import apiService from "../../service/Api";
import Pagination from "../../components/ui/Pagination";
import { accentForKey } from "../../constants/crmAccents";
import { toast } from "react-toastify";
import { IContact } from "../../types/contact";
import {
  downloadCompaniesCSV,
  downloadVCard,
  vCardFilename,
} from "../contacts/utils/vCard";

interface ICompany {
  id: number;
  name: string;
  email?: string;
  phone?: string;
  address?: string;
  industry?: string;
  logo?: string;
}
interface CompanyApiResponse {
  id: number;
  name: string;
  primary_email?: string | null;
  phone_number?: string | null;
  city?: string | null;
  country?: string | null;
  street_address?: string | null;
  company_type?: string | null;
  company_logo?: string | null;
}

const ListCompanies: React.FC = () => {
  const [companies, setCompanies] = useState<ICompany[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [filterCompanyType, setFilterCompanyType] = useState("all");
  const [filterCountry, setFilterCountry] = useState("all");
  const [companyTypes, setCompanyTypes] = useState<string[]>([]);
  const [countries, setCountries] = useState<string[]>([]);
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalItems, setTotalItems] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(12);
  const [, setIsExporting] = useState(false);
  const [isSelectionMode, setIsSelectionMode] = useState(false);
  const [selectedCompaniesMap, setSelectedCompaniesMap] = useState<
    Record<number, ICompany>
  >({});

  const selectedCompanyIds = React.useMemo(
    () => Object.keys(selectedCompaniesMap).map(Number),
    [selectedCompaniesMap],
  );

  const selectedCompanies = React.useMemo(
    () => Object.values(selectedCompaniesMap),
    [selectedCompaniesMap],
  );

  const allVisibleSelected = React.useMemo(
    () => companies.length > 0 && companies.every((company) => selectedCompanyIds.includes(company.id)),
    [companies, selectedCompanyIds],
  );
  const someVisibleSelected = React.useMemo(
    () => companies.some((company) => selectedCompanyIds.includes(company.id)),
    [companies, selectedCompanyIds],
  );

  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(
    null,
  );

  const reduxTheme = useSelector((state: RootState) => state.theme);
  const primaryColor = reduxTheme.primary_colors?.[400] || "#724E41";
  const primaryHoverColor = reduxTheme.primary_colors?.[600] || "#6b4e3d";
  const primaryTextColor = reduxTheme.primary_colors?.[400] || "#9c6644";
  const secondaryTextColor = reduxTheme.neutral_colors?.[100] || "#D6DDEB";
  const backgroundColor = reduxTheme.neutral_colors?.[900] || "#A9B2C3";

  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCompany, setSelectedCompany] = useState<ICompany | null>(null);

  const handleMenuClick = (
    event: React.MouseEvent<HTMLElement>,
    company: ICompany,
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedCompany(company);
  };

  const toggleCompanySelection = useCallback((company: ICompany) => {
    setSelectedCompaniesMap((prev) => {
      const next = { ...prev };
      if (next[company.id]) {
        delete next[company.id];
      } else {
        next[company.id] = company;
      }
      return next;
    });
  }, []);

  const exitSelectionMode = useCallback(() => {
    setSelectedCompaniesMap({});
    setIsSelectionMode(false);
  }, []);

  const toggleSelectAllVisible = useCallback(() => {
    setSelectedCompaniesMap((previous) => {
      const next = { ...previous };
      if (allVisibleSelected) companies.forEach((company) => delete next[company.id]);
      else companies.forEach((company) => { next[company.id] = company; });
      return next;
    });
  }, [allVisibleSelected, companies]);

  const handleExportMenuClick = (e: React.MouseEvent<HTMLElement>) =>
    setExportMenuAnchor(e.currentTarget);
  const handleExportMenuClose = () => setExportMenuAnchor(null);

  const handleExportSelectedContacts = useCallback(
    (format: "vcf" | "csv") => {
      if (selectedCompanies.length === 0) {
        toast.error("Select contacts before exporting.");
        return;
      }

      setIsExporting(true);

      try {
        const filename = `muzukuru-selected-contacts-${new Date()
          .toISOString()
          .slice(0, 10)}`;

        const contactsToExport = selectedCompanies.map(companyToContact);

        if (format === "csv") {
          downloadCompaniesCSV(contactsToExport, `${filename}.csv`);
        } else {
          downloadVCard(contactsToExport, `${filename}.vcf`);
        }

        toast.success(
          `${selectedCompanies.length} contact${
            selectedCompanies.length === 1 ? "" : "s"
          } exported ${format === "csv" ? "as CSV" : "as vCard"}`,
        );

        exitSelectionMode();
      } catch (e) {
        console.warn("Toast error:", e);
      } finally {
        setIsExporting(false);
      }
    },
    [selectedCompanies, exitSelectionMode],
  );

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCompany(null);
  };

  const handleEditCompany = (companyId: number) => {
    navigate(`/companies/${companyId}/edit`);
    handleMenuClose();
  };

  const handleDeleteCompany = async (companyId: number) => {
    if (!companyId) return;

    // Optional: confirm deletion with the user
    const confirmed = window.confirm(
      "Are you sure you want to delete this company?",
    );
    if (!confirmed) return;

    try {
      await apiService.delete(`api/companies/company/${companyId}/`);
      // Refresh the list after deletion
      fetchCompanies();
    } catch (err) {
      console.error("Failed to delete company:", err);
      toast("Failed to delete company. Please try again.");
    } finally {
      handleMenuClose();
    }
  };

  const companyToContact = (company: ICompany): IContact => ({
    id: company.id,
    name: company.name,
    first_name: null,
    last_name: null,
    email: company.email || null,
    phone: company.phone || null,
    salutation: null,
    contact_source: null,
    contact_type: "company",
    contactPerson: null,
    contactPersonAvatar: null,
    companyLogo: company.logo || null,
    industry: company.industry || null,
    website: null,
    taxNumber: null,
    dateOfBirth: null,
    city: null,
    completeness_score: 0,
    country: null,
    facebook_profile: null,
    phone_numbers: null,
    address: company.address || null,
    instagram_profile: null,
    twitter_profile: null,
    linkedin_profile: null,
    notes: null,
    profile_image_file: company.logo || null,
    category_type: null,
    company_id: null,
    company_name: company.name,
    company_email: company.email || null,
    created_at: new Date().toISOString(),
    created_by: null,
    updated_at: undefined,
    last_updated_by: null,
    type: "company",
    contact_image: company.logo || undefined,
  });

  const handleExportCompany = (company: ICompany) => {
    const contact = companyToContact(company);
    downloadVCard([contact], vCardFilename(contact));
    try {
      toast.success("Exported company vCard");
    } catch (e) {
      console.warn("Toast error:", e);
    }
  };

  const fetchCompanies = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const params: Record<string, string | number | undefined> = {
        page: currentPage,
        page_size: pageSize,
        search: searchTerm || undefined,
        company_type: filterCompanyType === "all" ? undefined : filterCompanyType,
        country: filterCountry === "all" ? undefined : filterCountry,
      };

      const res = await apiService.get<{
        count: number;
        results: CompanyApiResponse[];
      }>("api/companies/company/", params);

      const mappedCompanies: ICompany[] = res.data.results.map((company) => ({
        id: company.id,
        name: company.name,
        email: company.primary_email || undefined,
        phone: company.phone_number || undefined,
        address:
          company.city || company.country
            ? `${company.city || ""}, ${company.country || ""}`
                .trim()
                .replace(/^,|,$/g, "")
            : company.street_address || undefined,
        industry: company.company_type || undefined,
        logo: company.company_logo || undefined,
      }));

      setCompanies(mappedCompanies);
      setTotalItems(res.data.count);
    } catch (err: unknown) {
      const errorMessage =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Failed to load companies.";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, searchTerm, filterCompanyType, filterCountry]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        const response = await apiService.get<{ results: CompanyApiResponse[] }>(
          "api/companies/company/", { page: 1, page_size: 1500 },
        );
        setCompanyTypes([...new Set(response.data.results.map((company) => company.company_type).filter((value): value is string => Boolean(value)))].sort());
        setCountries([...new Set(response.data.results.map((company) => company.country).filter((value): value is string => Boolean(value)))].sort());
      } catch (err) {
        console.warn("Unable to load company filter options", err);
      }
    };
    fetchFilterOptions();
  }, []);

  const selectAllFilteredCompanies = useCallback(async () => {
    setIsLoading(true);
    try {
      const allMatches: CompanyApiResponse[] = [];
      let page = 1;
      let hasNextPage = true;
      while (hasNextPage) {
        const response = await apiService.get<{
          results: CompanyApiResponse[];
          next: string | null;
        }>("api/companies/company/", {
          page,
          page_size: 1000,
          search: searchTerm || undefined,
          company_type: filterCompanyType === "all" ? undefined : filterCompanyType,
          country: filterCountry === "all" ? undefined : filterCountry,
        });
        allMatches.push(...response.data.results);
        hasNextPage = Boolean(response.data.next);
        page += 1;
      }
      const matches = allMatches.map((company) => ({
        id: company.id,
        name: company.name,
        email: company.primary_email || undefined,
        phone: company.phone_number || undefined,
        address: company.city || company.country ? `${company.city || ""}, ${company.country || ""}`.trim().replace(/^,|,$/g, "") : company.street_address || undefined,
        industry: company.company_type || undefined,
        logo: company.company_logo || undefined,
      }));
      setSelectedCompaniesMap(Object.fromEntries(matches.map((company) => [company.id, company])));
      toast.success(`${matches.length} filtered compan${matches.length === 1 ? "y" : "ies"} selected.`);
    } catch {
      toast.error("Could not select the filtered companies. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [searchTerm, filterCompanyType, filterCountry]);

  const clearFilters = () => {
    setFilterCompanyType("all");
    setFilterCountry("all");
    setCurrentPage(1);
  };

  const filteredCompanies = companies; // API already handles search

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
      {/* Search & controls */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Box
          sx={{
            backgroundColor: "background.paper",
            border: `1.5px solid ${primaryColor}33`,
            borderRadius: "10px",
            display: "flex",
            alignItems: "center",
            pl: 1,
            flex: 1,
            maxWidth: 400,
            transition: "border-color 0.2s ease, box-shadow 0.2s ease",
            "&:focus-within": {
              borderColor: primaryColor,
              boxShadow: `0 0 0 3px ${primaryColor}22`,
            },
          }}
        >
          <SearchOutlined sx={{ mr: 1, color: primaryColor }} />
          <InputBase
            placeholder="Search companies..."
            sx={{ ml: 1, flex: 1, color: "text.primary" }}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
          />
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 2 }}>
          <Button
            variant="outlined"
            startIcon={<FilterListOutlined />}
            onClick={() => setFilterDrawerOpen(true)}
            sx={{ borderColor: primaryColor, color: primaryColor }}
          >
            Filter
          </Button>
          <IconButton
            onClick={() => setViewMode("grid")}
            sx={{
              color: viewMode === "grid" ? primaryColor : secondaryTextColor,
            }}
          >
            <GridViewIcon />
          </IconButton>
          <IconButton
            onClick={() => setViewMode("list")}
            sx={{
              color: viewMode === "list" ? primaryColor : secondaryTextColor,
            }}
          >
            <ViewListIcon />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate("/companies/add")}
            sx={{
              bgcolor: primaryColor,
              "&:hover": { bgcolor: primaryHoverColor },
              color: "#fff",
            }}
          >
            Add Company
          </Button>
        </Box>
      </Box>

      <Drawer
        anchor="right"
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        PaperProps={{ sx: { width: { xs: "100%", sm: 390 }, p: 3 } }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Box>
            <Typography variant="h6" fontWeight={700}>Filter companies</Typography>
            <Typography variant="body2" color="text.secondary">Narrow the list before selecting an export batch.</Typography>
          </Box>
          <IconButton onClick={() => setFilterDrawerOpen(false)} aria-label="Close filters"><Close /></IconButton>
        </Box>
        <Divider sx={{ mb: 3 }} />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="company-type-filter">Company type</InputLabel>
            <Select labelId="company-type-filter" label="Company type" value={filterCompanyType} onChange={(event) => { setFilterCompanyType(event.target.value); setCurrentPage(1); }}>
              <MenuItem value="all">All company types</MenuItem>
              {companyTypes.map((type) => <MenuItem key={type} value={type}>{type}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel id="company-country-filter">Country</InputLabel>
            <Select labelId="company-country-filter" label="Country" value={filterCountry} onChange={(event) => { setFilterCountry(event.target.value); setCurrentPage(1); }}>
              <MenuItem value="all">All countries</MenuItem>
              {countries.map((country) => <MenuItem key={country} value={country}>{country}</MenuItem>)}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ mt: "auto", pt: 3, display: "flex", gap: 1 }}>
          <Button fullWidth onClick={clearFilters}>Clear all</Button>
          <Button fullWidth variant="contained" onClick={() => setFilterDrawerOpen(false)} sx={{ bgcolor: primaryColor, "&:hover": { bgcolor: primaryHoverColor } }}>Show results</Button>
        </Box>
      </Drawer>

      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2, gap: 1 }}>
        {!isSelectionMode ? (
          <Button
            variant="outlined"
            onClick={() => setIsSelectionMode(true)}
            sx={{ borderColor: primaryColor, color: primaryColor }}
          >
            Select Companies to Export
          </Button>
        ) : null}
      </Box>

      {isSelectionMode && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
            gap: 2,
            p: 1.5,
            borderRadius: 2,
            bgcolor: "background.paper",
          }}
        >
          <Typography sx={{ color: primaryTextColor, fontWeight: 600 }}>
            {selectedCompanyIds.length === 0
              ? "Select companies to export"
              : `${selectedCompanyIds.length} selected`}
          </Typography>
          <Checkbox
            checked={allVisibleSelected}
            indeterminate={!allVisibleSelected && someVisibleSelected}
            onChange={toggleSelectAllVisible}
            sx={{ color: primaryColor, "&.Mui-checked": { color: primaryColor }, "&.MuiCheckbox-indeterminate": { color: primaryColor } }}
          />
          <Button size="small" onClick={selectAllFilteredCompanies} disabled={isLoading || totalItems === 0} sx={{ color: primaryColor, textTransform: "none" }}>
            Select all {totalItems} filtered
          </Button>
          <Box sx={{ display: "flex", gap: 1 }}>
            <Button variant="text" size="small" onClick={exitSelectionMode}>
              Cancel
            </Button>
            <Button
              variant="contained"
              size="small"
              disabled={selectedCompanyIds.length === 0}
              onClick={handleExportMenuClick}
              sx={{
                bgcolor: primaryColor,
                color: "#fff",
                "&:hover": { bgcolor: primaryHoverColor },
              }}
            >
              Export Selected
            </Button>
          </Box>
        </Box>
      )}

      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={handleExportMenuClose}
      >
        <MenuItem
          onClick={() => {
            handleExportSelectedContacts("vcf");
            handleExportMenuClose();
          }}
        >
          Export selected vCard
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleExportSelectedContacts("csv");
            handleExportMenuClose();
          }}
        >
          Export selected CSV
        </MenuItem>
      </Menu>

      {/* Loading/Error */}
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
          <CircularProgress sx={{ color: primaryColor }} />
        </Box>
      )}
      {error && (
        <Alert
          severity="error"
          sx={{ mb: 2, color: primaryTextColor, bgcolor: backgroundColor }}
        >
          {error}
          <Button color="inherit" size="small" onClick={fetchCompanies}>
            Retry
          </Button>
        </Alert>
      )}

      {!isLoading && filteredCompanies.length === 0 && (
        <Typography sx={{ textAlign: "center", mt: 4 }}>
          No companies found.
        </Typography>
      )}

      {/* Grid view */}
      {viewMode === "grid" && filteredCompanies.length > 0 && (
        <>
          <Grid container spacing={2}>
            {filteredCompanies.map((company) => {
              const accent = accentForKey(company.industry || company.name);
              const isSelected = selectedCompanyIds.includes(company.id);
              return (
                <Grid size={{ xs: 12, md: 3 }} key={company.id}>
                  <Card
                    sx={{
                      borderRadius: 3,
                      p: 2,
                      pt: 2.5,
                      minHeight: 250,
                      boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                      bgcolor: "background.paper",
                      border: `1px solid ${accent.border}`,
                      borderTop: `4px solid ${accent.solid}`,
                      borderTopWidth: 4,
                      position: "relative",
                      transition: "transform 0.2s ease, box-shadow 0.2s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: `0 10px 24px ${accent.border}`,
                      },
                      "&:hover .menu-button": {
                        opacity: 1,
                      },
                    }}
                  >
                    {isSelectionMode ? (
                      <Checkbox
                        checked={isSelected}
                        onClick={(event) => event.stopPropagation()}
                        onChange={() => toggleCompanySelection(company)}
                        sx={{
                          position: "absolute",
                          top: 6,
                          right: 6,
                          zIndex: 1,
                          color: accent.fg,
                          "&.Mui-checked": { color: primaryColor },
                        }}
                      />
                    ) : (
                      <IconButton
                        className="menu-button"
                        sx={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          opacity: 0,
                          transition: "opacity 0.2s",
                          bgcolor: "background.paper",
                          border: `1px solid ${accent.border}`,
                          "&:hover": { bgcolor: accent.bg },
                        }}
                        onClick={(e) => handleMenuClick(e, company)}
                      >
                        <MoreVert sx={{ color: accent.fg }} />
                      </IconButton>
                    )}

                    {/* Company avatar/content */}
                    <CardContent
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                      }}
                    >
                      <Avatar
                        src={company.logo}
                        sx={{
                          width: 80,
                          height: 80,
                          mb: 2,
                          bgcolor: accent.bg,
                          color: accent.fg,
                          border: `2px solid ${accent.border}`,
                        }}
                      >
                        {!company.logo && <BusinessIcon fontSize="large" />}
                      </Avatar>
                      <Typography variant="h6" align="center" fontWeight={600}>
                        {company.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        align="center"
                        sx={{ color: "text.secondary", mb: 0.5 }}
                      >
                        {company.email || "-"}
                      </Typography>
                      <Chip
                        label={company.industry || "Company"}
                        size="small"
                        sx={{
                          fontWeight: 700,
                          textTransform: "capitalize",
                          bgcolor: accent.bg,
                          color: accent.fg,
                          border: `1px solid ${accent.border}`,
                        }}
                      />
                    </CardContent>

                    <CardActions
                      sx={{
                        justifyContent: "center",
                        flexWrap: "wrap",
                        gap: 1,
                      }}
                    >
                      <Button
                        onClick={() => navigate(`/companies/${company.id}`)}
                        variant="contained"
                        disableElevation
                        sx={{
                          bgcolor: accent.solid,
                          color: "#fff",
                          textTransform: "none",
                          fontWeight: 600,
                          "&:hover": { bgcolor: accent.fg },
                        }}
                      >
                        View Company
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
          >
            <MenuItem
              onClick={() =>
                selectedCompany && handleEditCompany(selectedCompany.id)
              }
            >
              <EditOutlined sx={{ mr: 1 }} /> Edit
            </MenuItem>
            <MenuItem
              onClick={() => {
                if (selectedCompany) {
                  handleExportCompany(selectedCompany);
                }
                handleMenuClose();
              }}
            >
              <FileDownloadOutlined sx={{ mr: 1 }} /> Export vCard
            </MenuItem>
            <MenuItem
              onClick={() =>
                selectedCompany && handleDeleteCompany(selectedCompany.id)
              }
            >
              <DeleteOutline sx={{ mr: 1 }} /> Delete
            </MenuItem>
          </Menu>

          {!isLoading && (
            <Box sx={{ mt: 4 }}>
              <Pagination
                currentPage={currentPage - 1} // convert to 0-based for UI
                pageSize={pageSize}
                totalCount={totalItems}
                onPageChange={(page) => setCurrentPage(page + 1)} // convert back to 1-based
                onPageSizeChange={(size) => {
                  setPageSize(size);
                  setCurrentPage(1);
                }}
              />
            </Box>
          )}
        </>
      )}

      {/* List view */}
      {viewMode === "list" && filteredCompanies.length > 0 && (
        <Box>
          {filteredCompanies.map((company) => {
            const accent = accentForKey(company.industry || company.name);
            const isSelected = selectedCompanyIds.includes(company.id);
            return (
              <Card
                key={company.id}
                sx={{
                  mb: 2,
                  p: 2,
                  display: "flex",
                  alignItems: "center",
                  borderRadius: 2,
                  bgcolor: "background.paper",
                  borderLeft: `5px solid ${accent.solid}`,
                  border: `1px solid ${accent.border}`,
                  borderLeftWidth: 5,
                  transition: "box-shadow 0.2s ease",
                  "&:hover": { boxShadow: `0 6px 18px ${accent.border}` },
                }}
              >
                {isSelectionMode && (
                  <Checkbox
                    checked={isSelected}
                    onClick={(event) => event.stopPropagation()}
                    onChange={() => toggleCompanySelection(company)}
                    sx={{
                      mr: 1,
                      color: primaryColor,
                      "&.Mui-checked": { color: primaryColor },
                    }}
                  />
                )}
                <Avatar
                  src={company.logo}
                  sx={{
                    width: 50,
                    height: 50,
                    mr: 2,
                    bgcolor: accent.bg,
                    color: accent.fg,
                    border: `2px solid ${accent.border}`,
                  }}
                >
                  {!company.logo && <BusinessIcon />}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" fontWeight={600}>
                    {company.name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {company.email}
                  </Typography>
                  <Chip
                    label={company.industry || "Company"}
                    size="small"
                    sx={{
                      mt: 0.5,
                      fontWeight: 700,
                      textTransform: "capitalize",
                      bgcolor: accent.bg,
                      color: accent.fg,
                      border: `1px solid ${accent.border}`,
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <Button
                    onClick={() => navigate(`/companies/${company.id}`)}
                    variant="contained"
                    disableElevation
                    sx={{
                      bgcolor: accent.solid,
                      color: "#fff",
                      textTransform: "none",
                      fontWeight: 600,
                      "&:hover": { bgcolor: accent.fg },
                    }}
                  >
                    View Company
                  </Button>
                </Box>
              </Card>
            );
          })}
        </Box>
      )}
    </motion.div>
  );
};

export default ListCompanies;
